import React, { useId } from 'react';
import { Plus, Trash2, FolderPlus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select } from '../ui/select';
import { Switch } from '../ui/switch';

// Full operator set supported by feeds/rules.py compare()
export const OPERATORS = [
  ['equals', 'equals'], ['not_equals', "doesn't equal"],
  ['equals_any', 'equals any (a,b)'], ['not_equals_any', 'equals none of'],
  ['contains', 'contains'], ['not_contains', "doesn't contain"],
  ['contains_any', 'contains any (a,b)'], ['not_contains_any', 'contains none of'],
  ['starts_with', 'starts with'], ['ends_with', 'ends with'],
  ['greater_than', '>'], ['less_than', '<'],
  ['greater_or_equal', '≥'], ['less_or_equal', '≤'],
  ['length_exceeds', 'length >'], ['length_below', 'length <'],
  ['in_list', 'in list (a,b,c)'], ['not_in_list', 'not in list'],
  ['is_valid_gtin', 'is a valid GTIN'], ['is_not_valid_gtin', 'not a valid GTIN'],
  ['is_empty', 'is empty'], ['is_not_empty', "isn't empty"],
];
export const OP_LABEL = Object.fromEntries(OPERATORS);
export const NO_VALUE = new Set(['is_empty', 'is_not_empty', 'is_valid_gtin', 'is_not_valid_gtin']);
// Operators whose value is a LIST (newline- or comma-separated, per rules.py
// re.split(r'[\n,]', …)). Edited in a textarea, one value per line, so long
// brand/title lists stay readable instead of collapsing into one string.
export const LIST_VALUE = new Set(['contains_any', 'not_contains_any', 'equals_any',
  'not_equals_any', 'in_list', 'not_in_list']);

const EDIT_TYPES = [
  ['set', 'Set value'], ['replace', 'Find & replace'], ['combine', 'Combine fields'],
  ['calculate', 'Calculate (math)'], ['split', 'Split into list'],
  ['strip_html', 'Strip HTML'], ['slice', 'Slice list'],
];
const CALC_OPS = [['multiply_by', '×'], ['divide_by', '÷'], ['add', '+'], ['subtract', '−']];

// action (object | array) <-> {kind, list}
export const actionsOf = (action) => (Array.isArray(action) ? action : [action || {}]);
export const actionKind = (action) => {
  const t = (actionsOf(action)[0] || {}).type;
  return (t === 'exclude' || t === 'keep') ? t : 'modify';
};
const collapse = (list) => (list.length === 1 ? list[0] : list);

// Free-text field input that suggests known internal fields but accepts any
// output field name (price, custom_label_3, …).
export function FieldCombo({ value, onChange, fields, placeholder = 'field', className = '' }) {
  const id = useId();
  return (
    <div className={className}>
      <Input list={id} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-9 w-full" />
      <datalist id={id}>{(fields || []).map((f) => <option key={f.name || f} value={f.name || f} />)}</datalist>
    </div>
  );
}

// One edit action (set / replace / combine / calculate / split / strip_html / slice)
function ActionRow({ action, fields, onChange, onRemove, removable }) {
  const t = action.type || 'set';
  const set = (patch) => onChange({ ...action, ...patch });
  const retype = (nt) => {
    const base = { type: nt, field: action.field || '' };
    if (nt === 'set') base.value = action.value || '';
    if (nt === 'replace') Object.assign(base, { find: '', replace: '', regex: false });
    if (nt === 'combine') Object.assign(base, { sources: action.sources || [], separator: action.separator ?? ' ' });
    if (nt === 'calculate') Object.assign(base, { source: action.source || action.field || '', op: 'multiply_by', value: '' });
    if (nt === 'split') base.separator = action.separator ?? ',';
    if (nt === 'slice') Object.assign(base, { start: 0, end: '' });
    onChange(base);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background p-2">
      <Select value={t} onChange={(e) => retype(e.target.value)} className="h-9 w-40">
        {EDIT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      <FieldCombo value={action.field} fields={fields} onChange={(v) => set({ field: v })} className="w-44" />
      {t === 'set' && (
        <><span className="text-muted-foreground">=</span>
          <Input value={action.value || ''} onChange={(e) => set({ value: e.target.value })} placeholder="value or {template}" className="h-9 w-56" /></>
      )}
      {t === 'replace' && (
        <>
          <span className="text-xs text-muted-foreground">find</span>
          <Input value={action.find || ''} onChange={(e) => set({ find: e.target.value })} placeholder="find" className="h-9 w-32" />
          <span className="text-muted-foreground">→</span>
          <Input value={action.replace || ''} onChange={(e) => set({ replace: e.target.value })} placeholder="replace" className="h-9 w-32" />
          <label className="flex items-center gap-1 text-xs text-muted-foreground">regex <Switch checked={!!action.regex} onCheckedChange={(v) => set({ regex: v })} /></label>
        </>
      )}
      {t === 'combine' && (
        <>
          <span className="text-muted-foreground">= join(</span>
          <Input value={(action.sources || []).join(', ')} onChange={(e) => set({ sources: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="field1, field2" className="h-9 w-56" />
          <span className="text-muted-foreground">) sep</span>
          <Input value={action.separator ?? ' '} onChange={(e) => set({ separator: e.target.value })} className="h-9 w-16" />
        </>
      )}
      {t === 'calculate' && (
        <>
          <span className="text-muted-foreground">=</span>
          <FieldCombo value={action.source} fields={fields} onChange={(v) => set({ source: v })} placeholder="source" className="w-40" />
          <Select value={action.op || 'multiply_by'} onChange={(e) => set({ op: e.target.value })} className="h-9 w-24">
            {CALC_OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Input value={action.value ?? ''} onChange={(e) => set({ value: e.target.value })} placeholder="number"
            className="h-9 w-28" readOnly={action.auto === 'cogs_fx'} />
          <span className="text-[11px] text-muted-foreground">→ 2 dp</span>
          {action.auto === 'cogs_fx' && (
            <span
              className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700"
              title="Auto-managed rate. Re-baked every night from the live Pricython/ECB rate (the same source as the market prices), so this number always reflects the current EUR→currency rate. Any manual edit is overwritten on the next nightly run."
            >
              <RefreshCw className="h-3 w-3" /> auto rate · refreshed nightly from Pricython
            </span>
          )}
        </>
      )}
      {t === 'split' && (
        <><span className="text-xs text-muted-foreground">on separator</span>
          <Input value={action.separator ?? ''} onChange={(e) => set({ separator: e.target.value })} placeholder="e.g. ✔" className="h-9 w-24" /></>
      )}
      {t === 'strip_html' && <span className="text-xs text-muted-foreground">remove HTML markup → clean text (keeps paragraph breaks)</span>}
      {t === 'slice' && (
        <>
          <span className="text-muted-foreground">keep [</span>
          <Input value={action.start ?? 0} onChange={(e) => set({ start: e.target.value })} className="h-9 w-14" />
          <span className="text-muted-foreground">:</span>
          <Input value={action.end ?? ''} onChange={(e) => set({ end: e.target.value })} placeholder="end" className="h-9 w-14" />
          <span className="text-muted-foreground">]</span>
        </>
      )}
      {removable && <Button variant="ghost" size="icon" className="ml-auto" onClick={onRemove} aria-label="Remove action"><Trash2 className="h-4 w-4" /></Button>}
    </div>
  );
}

// THEN editor: pick filter (exclude/keep) OR modify one-or-more fields
export function ActionsEditor({ action, fields, onChange }) {
  const kind = actionKind(action);
  const list = kind === 'modify' ? actionsOf(action) : [];
  const setKind = (k) => {
    if (k === 'exclude' || k === 'keep') onChange({ type: k });
    else onChange({ type: 'set', field: '', value: '' });
  };
  const patchAt = (i, a) => onChange(collapse(list.map((x, j) => (j === i ? a : x))));
  const addAction = () => onChange(collapse([...list, { type: 'set', field: '', value: '' }]));
  const removeAt = (i) => {
    const next = list.filter((_, j) => j !== i);
    onChange(next.length ? collapse(next) : { type: 'set', field: '', value: '' });
  };
  return (
    <div className="space-y-2">
      <Select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 w-60">
        <option value="modify">Modify field(s)…</option>
        <option value="exclude">Exclude matching items</option>
        <option value="keep">Keep only matching items</option>
      </Select>
      {kind === 'modify' ? (
        <div className="space-y-2">
          {list.map((a, i) => (
            <ActionRow key={i} action={a} fields={fields} onChange={(na) => patchAt(i, na)} onRemove={() => removeAt(i)} removable={list.length > 1} />
          ))}
          <Button variant="ghost" size="sm" className="gap-1" onClick={addAction}><Plus className="h-4 w-4" /> Add action</Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{kind === 'exclude' ? 'Items matching the condition are removed from this feed.' : 'Only items matching the condition are kept.'}</p>
      )}
    </div>
  );
}

// IF editor: recursive AND/OR with nested groups
export function ConditionsEditor({ condition, fields, onChange, depth = 0 }) {
  const cond = condition && condition.conditions ? condition : { match: 'all', conditions: [] };
  const items = cond.conditions;
  const patch = (p) => onChange({ ...cond, ...p });
  const setItem = (i, v) => patch({ conditions: items.map((c, j) => (j === i ? v : c)) });
  const addLeaf = () => patch({ conditions: [...items, { field: '', operator: 'equals', value: '' }] });
  const addGroup = () => patch({ conditions: [...items, { match: 'any', conditions: [{ field: '', operator: 'equals', value: '' }] }] });
  const removeAt = (i) => patch({ conditions: items.filter((_, j) => j !== i) });
  return (
    <div className={depth ? 'rounded-md border-l-2 border-border pl-3' : ''}>
      <div className="mb-2 flex items-center gap-2 text-sm">
        <span>Match</span>
        <Select value={cond.match || 'all'} onChange={(e) => patch({ match: e.target.value })} className="h-8 w-auto">
          <option value="all">ALL (AND)</option>
          <option value="any">ANY (OR)</option>
        </Select>
        <span>of:</span>
      </div>
      <div className="space-y-2">
        {items.map((c, i) => (c.conditions ? (
          <div key={i} className="flex items-start gap-1">
            <div className="flex-1"><ConditionsEditor condition={c} fields={fields} onChange={(v) => setItem(i, v)} depth={depth + 1} /></div>
            <Button variant="ghost" size="icon" onClick={() => removeAt(i)} aria-label="Remove group"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ) : (
          <div key={i} className="flex items-start gap-2">
            <FieldCombo value={c.field} fields={fields} onChange={(v) => setItem(i, { ...c, field: v })} className="w-1/3" />
            <Select value={c.operator || 'equals'} onChange={(e) => setItem(i, { ...c, operator: e.target.value })} className="h-9 w-1/3">
              {OPERATORS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <span className="flex-1">{!NO_VALUE.has(c.operator) && (LIST_VALUE.has(c.operator)
              ? <Textarea value={c.value ?? ''} onChange={(e) => setItem(i, { ...c, value: e.target.value })}
                  rows={Math.min(12, Math.max(2, String(c.value ?? '').split('\n').length + 1))}
                  className="text-xs leading-5" placeholder="one value per line (or comma-separated)" />
              : <Input value={c.value ?? ''} onChange={(e) => setItem(i, { ...c, value: e.target.value })} className="h-9" placeholder="value" />)}</span>
            <Button variant="ghost" size="icon" onClick={() => removeAt(i)} aria-label="Remove condition"><Trash2 className="h-4 w-4" /></Button>
          </div>
        )))}
        {items.length === 0 && <p className="text-xs text-muted-foreground">No conditions — matches all items.</p>}
      </div>
      <div className="mt-2 flex gap-2">
        <Button variant="ghost" size="sm" className="gap-1" onClick={addLeaf}><Plus className="h-4 w-4" /> Condition</Button>
        <Button variant="ghost" size="sm" className="gap-1" onClick={addGroup}><FolderPlus className="h-4 w-4" /> Group (nested)</Button>
      </div>
    </div>
  );
}
